import { isEmpty } from 'lodash';
import { uniqBy } from 'lodash/fp';

import { ClubAffiliation, ClubDistrict, MembershipType } from './types';

export type DistrictType = {
  isHomeDistrict?: boolean;
  id: string;
  riDistrictId?: string;
  zone?: string;
};

export const getDistrictsList = (clubs?: ClubAffiliation[]) => {
  if (!clubs) return [];

  const clubLinkedDistricts = clubs
    .filter(club => (club.type as string) !== 'District')
    .flatMap(club => club.district || []);

  const selfIdentifiedDistricts = clubs
    .filter(club => (club.type as string) === 'District')
    .map(club => ({
      id: club.id,
      riDistrictId: club.riClubId,
      zone: club?.zone,
    }));

  const combined = [...clubLinkedDistricts, ...selfIdentifiedDistricts];

  return uniqBy(d => d.riDistrictId || d.id, combined);
};

export const getDistrictsOrder = (districts: DistrictType[]) =>
  [...districts].sort((a, b) => {
    if (a.isHomeDistrict === b.isHomeDistrict) {
      return Number(a.riDistrictId) - Number(b.riDistrictId);
    }
    if (a.isHomeDistrict) {
      return -1;
    }

    return 1;
  });

export const getOrderedDistrictsList = (clubs?: ClubAffiliation[]) => {
  if (!clubs) return [];

  const clubLinkedDistricts = clubs
    .filter(club => (club.type as string) !== 'District')
    .flatMap(club => club.district || []);

  const selfIdentifiedDistricts = clubs
    .filter(club => (club.type as string) === 'District')
    .map(club => ({
      id: club.id,
      riDistrictId: club.riClubId,
      zone: club?.zone,
    }));

  const combined = [...clubLinkedDistricts, ...selfIdentifiedDistricts];

  const homeDistricts = clubs
    .filter(club => {
      const affiliations = !isEmpty(club?.affiliation) ? club.affiliation : [];
      return affiliations.some(
        affiliation =>
          affiliation.role === MembershipType.Member ||
          affiliation.role === MembershipType.Rotaractor
      );
    })
    .flatMap(club => club.district || []);

  const homeDistrict = (!isEmpty(homeDistricts)
    ? homeDistricts[0]
    : {}) as ClubDistrict;
  const sanitizedDistricts = combined?.map(district => {
    const zone = district?.zone || '';
    const sanitizedZone = /[a-zA-Z]$/.test(zone) ? zone.slice(0, -1) : zone;
    if (!isEmpty(homeDistrict) && homeDistrict.id === district.id) {
      return { ...district, isHomeDistrict: true, zone: sanitizedZone };
    }
    return { ...district, isHomeDistrict: false, zone: sanitizedZone };
  });

  const sortedDistricts = getDistrictsOrder(sanitizedDistricts);
  return uniqBy(d => d.riDistrictId || d.id, sortedDistricts);
};

export const getDistrictDetailsPath = (districtId: string) =>
  `/domui/district/${districtId}/details`;
